import { defineStore } from "pinia";
import { doc, getDoc, getDocs, onSnapshot, setDoc, deleteDoc } from "firebase/firestore";
import _ from "lodash";
import { useFirestore } from "~/composables/useFirestore";
import { usePosShopStore } from "./pos.shop";
import { computed } from "vue";
import { SettingType } from "~/interface/Setting";
import { readMirror, writeMirror } from "~/services/repos/baseRepo";
import { registerResource, refreshResourceDebounced } from "~/services/repos/versionSync";
import { setActiveBillingStrategy } from "~/class/billing";

export const useSettingsStore = defineStore("settings", () => {
  const settings = ref<any[]>([]);
  const dynamodbSettings = ref([]);
  // togoNoService อ่านจาก DynamoDB (ไม่ใช่ MSSQL) โหลดใน finishLoad()
  const togoNoServiceValue = ref(false);
  const printerSettings = ref({});
  const printTimesSetting = ref({});
  const loaded = ref(false);
  const tables = ref([]);


  // Kept on the public surface for back-compat with any old caller. With the
  // new cache-first/version-sync flow there is no "slow network" semaphore —
  // syncAllVersions either serves cache or refetches before bootstrap unblocks.
  const lastFetchResult = ref<{ source: string; tooLong: boolean } | null>(null)

  const applySettings = (res: any) => {
    settings.value = res
    // settingCache.clear()
    computeInit()
  }

  /** Register the `settings` resource with versionSync. */
  const registerSettingsResource = () => {
    registerResource({
      resource: 'settings',
      refetch: () => useDataservice().settings.fetch(),
      applyToStore: (data: any) => applySettings(data),
    })
  }





  const getSystemEnvByName = (name: string) => {
    let env = settings.value.filter((item: any) => item.Name == name);
    env = _.sortBy(env, [function (o) { return o.Id; }]); //sort by id asc

    return env.length > 0 ? env[0] : null;
  }

  const storeName = computed(() => {
    return getStoreName()
  })

  // Single source of truth: lookup map รวมศูนย์ที่ Vue track ได้แน่นอน
  // ทุก computed ด้านล่างอ่านจาก settingsMap.value โดยตรง ไม่ผ่าน function ซ้อน
  const settingsMap = computed<Record<string, string>>(() => {
    const map: Record<string, string> = {}
    for (const item of settings.value || []) {
      if (item?.Name != null) map[item.Name] = item.Value
    }
    return map
  })

  const shopName = computed(() => settingsMap.value[SettingType.SHOP_NAME] || "")

  const printMoveTable = computed(() => settingsMap.value[SettingType.PRINT_MOVE_TABLE] === "1" || settingsMap.value[SettingType.PRINT_MOVE_TABLE] === "true")

  // อ่านจาก DynamoDB (togoNoServiceValue) — ตั้งค่าใน finishLoad() ไม่ใช่จาก MSSQL settingsMap
  const togoNoService = computed(() => togoNoServiceValue.value)

  const enableCustomerMode = computed(() => {
    return settingsMap.value[SettingType.CUSTOMER_MODE] === "true" || settingsMap.value[SettingType.CUSTOMER_MODE] === "1"
  })

  const isLockEnable = computed(() => settingsMap.value[SettingType.IS_LOCK_ENABLE] === "true" || settingsMap.value[SettingType.IS_LOCK_ENABLE] === "1")

  const smsReport = computed(() => settingsMap.value[SettingType.SMS_REPORT] === "true" || settingsMap.value[SettingType.SMS_REPORT] === "1")


  // JOB mode toggle (setting name "EnableJob"). When on, the POS shows the JOB
  // tab instead of TOGO (the two modes are mutually exclusive).
  const isJobEnabled = computed(() =>   settingsMap.value[SettingType.USE_JOB] === "true" || settingsMap.value[SettingType.USE_JOB] === "1")

  // Combo-set editor toggle (system env "UseComboSet"). When on, the Menu → Combo
  // editor tab is shown. Port of legacy config.parameter.useComboSet ('true') —
  // see Views/Menu/Index.cshtml. Shops without the flag never see the tab.
  const isComboSetEnabled = computed(() =>  settingsMap.value[SettingType.USE_COMBO_SET] === "true" || settingsMap.value[SettingType.USE_COMBO_SET] === "1")

  const voidOnMobile = computed(() => settingsMap.value[SettingType.VOID_ON_MOBILE] === "true" || settingsMap.value[SettingType.VOID_ON_MOBILE] === "1")

  const printPreviewRemote = computed(() => settingsMap.value[SettingType.PRINT_PREVIEW_REMOTE] === "true" || settingsMap.value[SettingType.PRINT_PREVIEW_REMOTE] === "1")

  // Room Service toggle (system env "RoomEnable"). When on, the payment modal shows
  // an optional free-text Room input; its value is stamped onto the receipt as
  // ReceiptOrder.Room and printed on the slip. Port of legacy config.parameter.roomEnable.
  const isRoomEnabled = computed(() => settingsMap.value[SettingType.ROOM_ENABLE] === "true" || settingsMap.value[SettingType.ROOM_ENABLE] === "1")

  // Per-line item discount on the receipt (system env "ShowItemDiscount").
  // Default OFF — shops opt in from Settings → Tab 1.
  const showItemDiscount = computed(() => settingsMap.value[SettingType.SHOW_ITEM_DISCOUNT] === "true" || settingsMap.value[SettingType.SHOW_ITEM_DISCOUNT] === "1")

  // Price on the KITCHEN slip (system env "PrintShowPrice"). Default OFF; set by
  // the admin backend only, so there is no settings-page entry for it.
  const printShowPrice = computed(() => settingsMap.value[SettingType.PRINT_SHOW_PRICE] === "true" || settingsMap.value[SettingType.PRINT_SHOW_PRICE] === "1")

  const roundingMode = computed(() => {

    if ( settingsMap.value[SettingType.NO_ROUND_UP] === "none" 
      || settingsMap.value[SettingType.NO_ROUND_UP] === "1" || settingsMap.value[SettingType.NO_ROUND_UP] === "true"
    ) {
      return "none"
    }

    if ( settingsMap.value[SettingType.NO_ROUND_UP] === "0" || settingsMap.value[SettingType.NO_ROUND_UP] === "5/4") {
      return "5/4"
    }

    if (settingsMap.value[SettingType.NO_ROUND_UP] === "up") {
      return "up"
    }

    if (settingsMap.value[SettingType.NO_ROUND_UP] === "down") {
      return "down"
    }

    return "5/4"
  })

  const taxMode = computed(() => parseInt(settingsMap.value["TaxMode"] || "0"))
  const taxRate = computed(() => parseInt(settingsMap.value["TaxRate1"] || "0"))
  const serviceCharge = computed(() => parseInt(settingsMap.value["ServiceCharge"] || "0"))

  // Per-shop billing-calculation strategy (feature flag). Empty/unknown => the
  // default pipeline. e.g. "divana" selects DivanaBillingStrategy. Pushed into
  // the billing module once at startup by applyBillingStrategy() — it is fixed
  // per shop, so it does NOT ride along in tableBillingGlobal.
  const billingStrategy = computed(() => (settingsMap.value[SettingType.BILLING_PATTERN] || "").trim().toLowerCase())

  const setStoreNameAtNavBar = () => {
    const name =
      settings.value.find((item: any) => item.Name == "ShopName")?.Value || "";

    //set at navbar
    // const storeName = useState("storeName", () => "Niceloop POS");
    // storeName.value = name;
  };

  const getStoreName = () => {
    return (
      settings.value.find((item: any) => item.Name == "ShopName")?.Value || ""
    );
  };

  const computeTable = () => {

    const tableArrayData =
      settings.value.find((item: any) => item.Name == "TableArray")?.Value ||
      "";

    // zone table array
    if (tableArrayData) {
      const tableArray = tableArrayData.split(",");
      const tableArrayDataConverted = tableArray.map(
        (item: any, index: any) => {
          return {
            name: String.fromCharCode(65 + index),
            tableCount: parseInt(item),
          };
        }
      );
      tables.value = _.filter(
        tableArrayDataConverted,
        (item: any) => item.tableCount > 0
      );
    } else {

      // single table
      let count =
        settings.value.find((item: any) => item.Name == "Table")?.Value || 100;
      const parsed = parseInt(count) || 0;
      tables.value = [
        {
          name: "T",
          // Never render fewer than 30 tables — small shops (e.g. 6) need room.
          tableCount: Math.max(30, parsed),
        },
      ];
    }
  };

  function computeInit() {
    computeTable();
    setStoreNameAtNavBar();
    setGlobalBillingSetting();
    applyBillingStrategy();



  }

  const init = () => {
    // Just register. Bootstrap's syncAllVersions handles the actual fetch
    // and calls applyToStore. The dynamodb-backed timer setting is loaded
    // separately by `finishLoad()` because it isn't version-tracked.
    registerSettingsResource()
  }

  /**
   * After a successful settings mutation, refetch + bump the Firestore hash
   * so other terminals invalidate their caches on next bootstrap.
   * Called by components after `dataservice.systemEnv.put`.
   */
  const refresh = () => {
    refreshResourceDebounced('settings')
  }

  /**
   * Called by bootstrap after syncAllVersions resolves. Loads the dynamodb
   * timer setting (not version-tracked, cache-first) and marks the store
   * loaded.
   */
  const finishLoad = async () => {
    const timerSetting = await fetchDynamodbSettingWithKey("timer")
    computeTimeShiftSetting(timerSetting)

    
    // togoNoService — DynamoDB record { name: "togoNoService", value: true }
    const togoData = await fetchDynamodbSettingWithKey("togoNoService")
    togoNoServiceValue.value = parseTogoNoService(togoData)

    computeInit()
    loaded.value = true
  }

  const parseTogoNoService = (data: any): boolean => {
    if (!Array.isArray(data)) return false
    const rec = data.find((r: any) => r.name === "togoNoService")
    const v = rec?.value
    return v === true || v === "true" || v === "1" || v === 1
  }


  const setGlobalBillingSetting = () => {
    const setting = usePosTableStatesStore()
    setting.tableBillingGlobal = {
      sc: [0, serviceCharge.value],
      vat: [taxMode.value, taxRate.value],
      roundingMode: roundingMode.value,
    };
    return setting
  };

  /**
   * Resolve the shop's billing strategy once, at startup. Unlike sc / vat /
   * roundingMode this is NOT part of tableBillingGlobal: it is a property of the
   * shop, fixed for the life of the page, and no cashier action can override it
   * per table. Storing it in the billing module keeps it out of every bill's
   * options object. Unknown / empty => default pipeline.
   */
  const applyBillingStrategy = () => {
    setActiveBillingStrategy(billingStrategy.value)
  };

  /**
   * Dynamodb-backed settings are NOT version-tracked. Cache-first: serve
   * IDB if present, otherwise fetch and mirror. Refresh happens via
   * `saveDynamoSetting` (which overwrites the cache after a successful PUT).
   */
  const fetchDynamodbSettingWithKey = async (key) => {
    const resource = `dynamoSettings_${key}`
    const cached = await readMirror<any[]>(resource)
    if (cached != null) return cached

    const shopStore = usePosShopStore();
    const params = {
      partitionKey: shopStore.storeId,
      sortKey: key
    }
    try {
      const fresh = (await useDataservice().settingsDynamo.fetch(params)) || []
      await writeMirror(resource, fresh)
      return fresh
    } catch (err) {
      console.warn(`[settings:${resource}] fetch failed`, err)
      return []
    }
  }

  const saveDynamoSetting = async (sortKey: string, value: any) => {
    const shopStore = usePosShopStore()
    const storeId = shopStore.storeId

    const existing = await fetchDynamodbSettingWithKey(sortKey)
    const record = Array.isArray(existing)
      ? existing.find((r: any) => r.name === sortKey)
      : null

    const payload = {
      ...(record || {}),
      customerId: storeId,
      name: sortKey,
      value,
    }

    if (record) {
      await useDataservice().settingsDynamo.putByDynamoFormat(storeId, sortKey, payload)
    } else {
      await useDataservice().settingsDynamo.postByDynamoFormat(storeId, payload)
    }

    // Invalidate the cache for this key so the next read fetches fresh data.
    // dynamoSettings_* aren't version-tracked, so this is the only refresh path.
    const params = { partitionKey: storeId, sortKey }
    try {
      const fresh = (await useDataservice().settingsDynamo.fetch(params)) || []
      await writeMirror(`dynamoSettings_${sortKey}`, fresh)
    } catch (err) {
      console.warn(`[settings:dynamoSettings_${sortKey}] post-save refetch failed`, err)
    }
  }

  const computeTimeShiftSetting = async (setting: any) => {
    // TODO: Remove this when the API is fixed
    if (Array.isArray(setting) && setting.length > 0) {
      let timerSetting = setting.find((time: any) => time.name === "timer")
      printTimesSetting.value = timerSetting?.value || {}
      return timerSetting?.value || {}
    }
  }

  return {
    settings,
    printTimesSetting,
    tables,
    loaded,
    init,
    finishLoad,
    refresh,
    getStoreName,
    storeName,

    printerSettings,
    getSystemEnvByName,
    saveDynamoSetting,
    fetchDynamodbSettingWithKey,
    computeTable,

    shopName,
    printMoveTable,
    togoNoService,
    enableCustomerMode,
    isLockEnable,
    smsReport,
    isJobEnabled,
    isComboSetEnabled,
    voidOnMobile,
    printPreviewRemote,
    isRoomEnabled,
    showItemDiscount,
    printShowPrice,
    roundingMode,
    taxMode,
    taxRate,
    serviceCharge,
    billingStrategy,
    lastFetchResult,
  };
});
